import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    StudySession,
    CreateStudySessionRequest,
    UpdateStudySessionRequest,
    CompleteStudySessionRequest,
    SubmitVocabularyAnswerRequest,
    StudySessionCompletionResponse,
    VocabularyAttempt
} from '@/services/vocabularyService';

// === STUDY SESSIONS ACTIONS ===
export const createStudySessionRequest = createAction<CreateStudySessionRequest>(
    'studySessions/createStudySessionRequest',
);
export const createStudySessionSuccess = createAction<StudySession>('studySessions/createStudySessionSuccess');
export const createStudySessionFailure = createAction<string>('studySessions/createStudySessionFailure');

export const updateStudySessionRequest = createAction<UpdateStudySessionRequest>(
    'studySessions/updateStudySessionRequest',
);
export const updateStudySessionSuccess = createAction<StudySession>('studySessions/updateStudySessionSuccess');
export const updateStudySessionFailure = createAction<string>('studySessions/updateStudySessionFailure');

export const fetchStudySessionsRequest = createAction<{ userId?: number; groupId?: string }>(
    'studySessions/fetchStudySessionsRequest',
);
export const fetchStudySessionsSuccess = createAction<StudySession[]>('studySessions/fetchStudySessionsSuccess');
export const fetchStudySessionsFailure = createAction<string>('studySessions/fetchStudySessionsFailure');

export const completeStudySessionRequest = createAction<{ sessionId: string; completionData?: CompleteStudySessionRequest }>(
    'studySessions/completeStudySessionRequest',
);
export const completeStudySessionSuccess = createAction<StudySessionCompletionResponse>('studySessions/completeStudySessionSuccess');
export const completeStudySessionFailure = createAction<string>('studySessions/completeStudySessionFailure');

export const submitVocabularyAnswerRequest = createAction<SubmitVocabularyAnswerRequest>(
    'studySessions/submitVocabularyAnswerRequest',
);
export const submitVocabularyAnswerSuccess = createAction<VocabularyAttempt>('studySessions/submitVocabularyAnswerSuccess');
export const submitVocabularyAnswerFailure = createAction<string>('studySessions/submitVocabularyAnswerFailure');

// === STATE INTERFACE ===
export interface StudySessionsState {
    studySession: StudySession | null;
    sessionCompletionData: StudySessionCompletionResponse | null;
    currentAttempt: VocabularyAttempt | null;

    // Loading States
    isLoadingSessions: boolean;
    isCreatingSession: boolean;
    isUpdatingSession: boolean;
    isCompletingSession: boolean;
    isSubmittingAnswer: boolean;

    // Error States
    sessionError: string | null;
}

const initialState: StudySessionsState = {
    studySession: null,
    sessionCompletionData: null,
    currentAttempt: null,
    isLoadingSessions: false,
    isCreatingSession: false,
    isUpdatingSession: false,
    isCompletingSession: false,
    isSubmittingAnswer: false,
    sessionError: null,
};

const studySessionsSlice = createSlice({
    name: 'studySessions',
    initialState,
    reducers: {
        setCurrentStudySession: (state, action: PayloadAction<StudySession | null>) => {
            state.studySession = action.payload;
        },
        clearSessionError: (state) => {
            state.sessionError = null;
        },
        resetAttempt: (state) => {
            state.currentAttempt = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === CREATE STUDY SESSION ===
            .addCase(createStudySessionRequest, (state) => {
                state.isCreatingSession = true;
                state.sessionError = null;
            })
            .addCase(createStudySessionSuccess, (state, action) => {
                state.isCreatingSession = false;
                state.studySession = action.payload;
            })
            .addCase(createStudySessionFailure, (state, action) => {
                state.isCreatingSession = false;
                state.sessionError = action.payload;
            })

            // === UPDATE STUDY SESSION ===
            .addCase(updateStudySessionRequest, (state) => {
                state.isUpdatingSession = true;
                state.sessionError = null;
            })
            .addCase(updateStudySessionSuccess, (state, action) => {
                if (action.payload) {
                    state.studySession = action.payload;
                }
            })
            .addCase(updateStudySessionFailure, (state, action) => {
                state.isUpdatingSession = false;
                state.sessionError = action.payload;
            })

            // === FETCH STUDY SESSIONS ===
            .addCase(fetchStudySessionsRequest, (state) => {
                state.isLoadingSessions = true;
                state.sessionError = null;
            })
            .addCase(fetchStudySessionsSuccess, (state, action) => {
                state.isLoadingSessions = false;
                state.studySession = action.payload[0];
            })
            .addCase(fetchStudySessionsFailure, (state, action) => {
                state.isLoadingSessions = false;
                state.sessionError = action.payload;
            })

            // === COMPLETE STUDY SESSION ===
            .addCase(completeStudySessionRequest, (state) => {
                state.isCompletingSession = true;
                state.sessionError = null;
            })
            .addCase(completeStudySessionSuccess, (state, action) => {
                state.isCompletingSession = false;
                state.sessionCompletionData = action.payload;
            })
            .addCase(completeStudySessionFailure, (state, action) => {
                state.isCompletingSession = false;
                state.sessionError = action.payload;
            })

            // === SUBMIT VOCABULARY ANSWER ===
            .addCase(submitVocabularyAnswerRequest, (state) => {
                state.isSubmittingAnswer = true;
                state.sessionError = null;
            })
            .addCase(submitVocabularyAnswerSuccess, (state, action) => {
                state.isSubmittingAnswer = false;
                state.currentAttempt = action.payload;
                console.log('aaaa', action.payload)
            })
            .addCase(submitVocabularyAnswerFailure, (state, action) => {
                state.isSubmittingAnswer = false;
                state.sessionError = action.payload;
            });
    },
});

export const { setCurrentStudySession, clearSessionError, resetAttempt } = studySessionsSlice.actions;

export default studySessionsSlice.reducer;
